import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  UserIcon,
  ShieldCheckIcon,
  CreditCardIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';
import CreditCardRegistration from '../../components/CreditCardRegistration';
import { API_BASE } from '../../lib/config';

const API = `${API_BASE}`;

const EmployerAccountSettings = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [employerProfile, setEmployerProfile] = useState(null);
  const [twoFactorAuth, setTwoFactorAuth] = useState({
    email: false,
    sms: false,
    phone: ''
  });
  const [profileData, setProfileData] = useState({
    business_name: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    prefecture: '',
    city: '',
    description: '',
    // 新規追加項目
    title: '', // 事業者タイトル（最大50文字）
    hourly_rate: '', // 時給
    work_hours: '', // 勤務時間
    work_location: '', // 勤務地
    message_to_jobseekers: '', // 求職者へのメッセージ（任意）
    thumbnail_images: [], // サムネイル画像（5枚まで）
    promotion_video: null // プロモーション動画（1つまで）
  });

  useEffect(() => {
    if (!user || user.user_type !== 'employer') {
      toast.error('事業者権限が必要です');
      navigate('/');
      return;
    }
    
    fetchEmployerProfile();
  }, [user, navigate]);

  const fetchEmployerProfile = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/employer-profiles`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const profiles = await response.json();
        const myProfile = profiles.find(p => p.employer_id === user.id);
        if (myProfile) {
          setEmployerProfile(myProfile);
          setProfileData({
            business_name: myProfile.business_name || '',
            contact_email: myProfile.contact_email || user.email,
            contact_phone: myProfile.contact_phone || '',
            address: myProfile.address || '',
            prefecture: myProfile.prefecture || '',
            city: myProfile.city || '',
            description: myProfile.description || '',
            // 新規追加項目
            title: myProfile.title || '',
            hourly_rate: myProfile.hourly_rate || '',
            work_hours: myProfile.work_hours || '',
            work_location: myProfile.work_location || '',
            message_to_jobseekers: myProfile.message_to_jobseekers || '',
            thumbnail_images: myProfile.thumbnail_images || [],
            promotion_video: myProfile.promotion_video || null
          });
        }
      }
    } catch (err) {
      console.error('Error fetching employer profile:', err);
      toast.error('プロフィールの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const url = employerProfile 
        ? `${API}/employer-profiles/${employerProfile.id}`
        : `${API}/employer-profiles`;
      
      const response = await fetch(url, {
        method: employerProfile ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(profileData)
      });
      
      if (response.ok) {
        toast.success('プロフィールを更新しました');
        fetchEmployerProfile();
      } else {
        throw new Error('プロフィールの更新に失敗しました');
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      toast.error('プロフィールの更新に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleTwoFactorToggle = async (type) => {
    try {
      // 2段階認証の設定をトグル（実装例）
      const newSettings = {
        ...twoFactorAuth,
        [type]: !twoFactorAuth[type]
      };
      setTwoFactorAuth(newSettings);
      
      // バックエンドへの送信は実装に応じて
      toast.success(`${type === 'email' ? 'メール' : 'SMS'}認証を${newSettings[type] ? '有効' : '無効'}にしました`);
    } catch (err) {
      toast.error('2段階認証の設定に失敗しました');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('ログアウトしました');
  };

  // 画像アップロード処理
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (profileData.thumbnail_images.length + files.length > 5) {
      toast.error('画像は最大5枚まで登録できます');
      return;
    }
    
    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) { // 5MB制限
        toast.error('画像サイズは5MB以下にしてください');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileData(prev => ({
          ...prev,
          thumbnail_images: [...prev.thumbnail_images, e.target.result.split(',')[1]]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  // 画像削除処理
  const removeImage = (index) => {
    setProfileData(prev => ({
      ...prev,
      thumbnail_images: prev.thumbnail_images.filter((_, i) => i !== index)
    }));
  };

  // 動画アップロード処理
  const handleVideoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 50 * 1024 * 1024) { // 50MB制限
      toast.error('動画サイズは50MB以下にしてください');
      return;
    }
    
    setProfileData(prev => ({
      ...prev,
      promotion_video: file
    }));
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">事業者プロフィール編集</h3>
            <form onSubmit={handleProfileUpdate} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  事業所名・店舗名 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={profileData.business_name}
                  onChange={(e) => setProfileData(prev => ({ ...prev, business_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="株式会社〇〇、〇〇店"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    連絡先メールアドレス
                  </label>
                  <input
                    type="email"
                    value={profileData.contact_email}
                    onChange={(e) => setProfileData(prev => ({ ...prev, contact_email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    連絡先電話番号
                  </label>
                  <input
                    type="tel"
                    value={profileData.contact_phone}
                    onChange={(e) => setProfileData(prev => ({ ...prev, contact_phone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="03-0000-0000"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    都道府県
                  </label>
                  <input
                    type="text"
                    value={profileData.prefecture}
                    onChange={(e) => setProfileData(prev => ({ ...prev, prefecture: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="東京都"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    市区町村
                  </label>
                  <input
                    type="text"
                    value={profileData.city}
                    onChange={(e) => setProfileData(prev => ({ ...prev, city: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="渋谷区"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  住所・所在地
                </label>
                <input
                  type="text"
                  value={profileData.address}
                  onChange={(e) => setProfileData(prev => ({ ...prev, address: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="詳細な住所を入力してください"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  事業紹介
                </label>
                <textarea
                  value={profileData.description}
                  onChange={(e) => setProfileData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows="4"
                  placeholder="事業内容や特徴について詳しく入力してください"
                  maxLength="300"
                />
                <p className="text-sm text-gray-500 mt-1">{profileData.description.length}/300文字</p>
              </div>

              {/* 新規追加項目 */}
              <div className="border-t border-gray-200 pt-6">
                <h4 className="text-lg font-medium text-gray-900 mb-4">求人詳細情報</h4>
                
                {/* 事業者タイトル */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    事業者タイトル *
                  </label>
                  <input
                    type="text"
                    value={profileData.title}
                    onChange={(e) => setProfileData(prev => ({ ...prev, title: e.target.value.slice(0, 50) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="求職者に表示されるタイトル"
                    maxLength="50"
                    required
                  />
                  <p className="text-sm text-gray-500 mt-1">{profileData.title.length}/50文字</p>
                </div>

                {/* 勤務時間・時給 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      勤務時間
                    </label>
                    <input
                      type="text"
                      value={profileData.work_hours}
                      onChange={(e) => setProfileData(prev => ({ ...prev, work_hours: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="9:00〜17:00"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      時給（円）
                    </label>
                    <input
                      type="number"
                      value={profileData.hourly_rate}
                      onChange={(e) => setProfileData(prev => ({ ...prev, hourly_rate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="1200"
                      min="0"
                    />
                  </div>
                </div>

                {/* 勤務地 */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    勤務地（アクセス情報）
                  </label>
                  <input
                    type="text"
                    value={profileData.work_location}
                    onChange={(e) => setProfileData(prev => ({ ...prev, work_location: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="渋谷駅から徒歩5分"
                  />
                </div>

                {/* 求職者へのメッセージ */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    求職者へのメッセージ（任意）
                  </label>
                  <textarea
                    value={profileData.message_to_jobseekers}
                    onChange={(e) => setProfileData(prev => ({ ...prev, message_to_jobseekers: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows="3"
                    placeholder="求職者の方へのメッセージをご記入ください"
                  />
                </div>

                {/* サムネイル画像アップロード */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    サムネイル画像（最大5枚）
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                      className="hidden"
                      id="thumbnail-upload"
                    />
                    <label
                      htmlFor="thumbnail-upload"
                      className="cursor-pointer text-blue-600 hover:text-blue-700 font-medium"
                    >
                      クリックして画像を選択
                    </label>
                    <p className="text-sm text-gray-500 mt-1">PNG, JPG, GIF（最大5MB）</p>
                  </div>
                  {profileData.thumbnail_images.length > 0 && (
                    <div className="mt-4 grid grid-cols-3 md:grid-cols-5 gap-4">
                      {profileData.thumbnail_images.map((image, index) => (
                        <div key={index} className="relative">
                          <img
                            src={typeof image === 'string' ? `data:image/jpeg;base64,${image}` : URL.createObjectURL(image)}
                            alt={`サムネイル ${index + 1}`}
                            className="w-full h-20 object-cover rounded-lg"
                          />
                          <button
                            onClick={() => removeImage(index)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                          >
                            
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* プロモーション動画アップロード */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    プロモーション動画（1つまで）
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <input
                      type="file"
                      accept="video/*"
                      onChange={handleVideoUpload}
                      className="hidden"
                      id="video-upload"
                    />
                    <label
                      htmlFor="video-upload"
                      className="cursor-pointer text-blue-600 hover:text-blue-700 font-medium"
                    >
                      クリックして動画を選択
                    </label>
                    <p className="text-sm text-gray-500 mt-1">MP4, MOV, AVI（最大50MB）</p>
                  </div>
                  {profileData.promotion_video && (
                    <div className="mt-4">
                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-700">
                          {profileData.promotion_video.name || 'プロモーション動画'}
                        </span>
                        <button
                          onClick={() => setProfileData(prev => ({ ...prev, promotion_video: null }))}
                          className="text-red-600 hover:text-red-700 text-sm font-medium"
                        >
                          削除
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {loading ? '更新中...' : 'プロフィールを更新'}
                </button>
              </div>
            </form>
          </div>
        );

      case 'security':
        return (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">2段階認証設定</h3>
            <div className="space-y-6">
              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center">
                  <EnvelopeIcon className="w-6 h-6 text-blue-600 mr-3" />
                  <div>
                    <h4 className="font-medium text-gray-900">メール認証</h4>
                    <p className="text-sm text-gray-600">ログイン時にメールでの認証を要求</p>
                  </div>
                </div>
                <button
                  onClick={() => handleTwoFactorToggle('email')}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    twoFactorAuth.email ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      twoFactorAuth.email ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center">
                  <PhoneIcon className="w-6 h-6 text-green-600 mr-3" />
                  <div>
                    <h4 className="font-medium text-gray-900">SMS認証</h4>
                    <p className="text-sm text-gray-600">ログイン時にSMSでの認証を要求</p>
                  </div>
                </div>
                <button
                  onClick={() => handleTwoFactorToggle('sms')}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    twoFactorAuth.sms ? 'bg-green-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      twoFactorAuth.sms ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {twoFactorAuth.sms && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    SMS認証用電話番号
                  </label>
                  <input
                    type="tel"
                    value={twoFactorAuth.phone}
                    onChange={(e) => setTwoFactorAuth(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="090-0000-0000"
                  />
                </div>
              )}
            </div>
          </div>
        );

      case 'payment':
        return (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">支払い用クレジットカード登録</h3>
            <p className="text-gray-600 mb-6">
              求職者への報酬支払いに使用するクレジットカードを登録してください。
            </p>
            {/* 統一コンポーネントをページ内に直接表示（ローカル/本番で同一UI） */}
            <CreditCardRegistration />
          </div>
        );

      case 'login':
        return (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">ログイン管理</h3>
            <div className="space-y-6">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">現在のログイン情報</h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>メールアドレス: {user.email}</p>
                  <p>ユーザータイプ: 事業者</p>
                  <p>アカウント名: {user.name}</p>
                </div>
              </div>

              <div className="border-t pt-6">
                <h4 className="font-medium text-gray-900 mb-4">アカウント操作</h4>
                <div className="space-y-3">
                  <button
                    onClick={() => navigate('/forgot-password')}
                    className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div>
                      <p className="font-medium text-gray-900">パスワードを変更</p>
                      <p className="text-sm text-gray-600">セキュリティ強化のため定期的にパスワードを変更してください</p>
                    </div>
                  </button>

                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-3 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                  >
                    <div>
                      <p className="font-medium text-red-600">ログアウト</p>
                      <p className="text-sm text-red-500">現在のセッションを終了します</p>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        {/* ヘッダー */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/employer-dashboard')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            事業者ダッシュボードに戻る
          </button>
          <h1 className="text-3xl font-bold text-gray-900">ログイン / アカウント設定</h1>
          <p className="text-gray-600 mt-2">事業者アカウントの設定を管理します</p>
        </div>

        {/* タブナビゲーション */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {[
                { id: 'profile', name: 'プロフィール', icon: BuildingOfficeIcon },
                { id: 'security', name: '2段階認証', icon: ShieldCheckIcon },
                { id: 'payment', name: 'クレジットカード', icon: CreditCardIcon },
                { id: 'login', name: 'ログイン管理', icon: UserIcon }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="w-5 h-5 mr-2" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* タブコンテンツ */}
        {renderTabContent()}
      </div>
    </div>
  );
};

export default EmployerAccountSettings;